import { useCallback, useEffect, useRef } from 'react';

/**
 * Ref callback that hands the node over only once the browser is idle.
 *
 * Embla (and similar libraries) measure their element as soon as the ref is
 * set — that happens right after React committed the DOM, before the browser
 * has laid the page out, so the measurement forces a full synchronous layout
 * (Lighthouse: "forced reflow"). Waiting for an idle moment means the browser
 * has already laid out, and the measuring work stays off the critical path.
 */
export default function useDeferredRef(attach) {
  const node = useRef(null);
  const scheduled = useRef(null);

  const cancel = useCallback(() => {
    if (!scheduled.current) return;
    const { type, id } = scheduled.current;
    scheduled.current = null;
    if (type === 'idle') window.cancelIdleCallback(id);
    else window.clearTimeout(id);
  }, []);

  useEffect(() => cancel, [cancel]);

  return useCallback(
    (next) => {
      cancel();
      node.current = next;
      // On unmount the library's own effect cleanup releases the instance.
      if (next === null) return;

      const run = () => {
        scheduled.current = null;
        attach(node.current);
      };
      scheduled.current =
        typeof window.requestIdleCallback === 'function'
          ? { type: 'idle', id: window.requestIdleCallback(run, { timeout: 1200 }) }
          : { type: 'timeout', id: window.setTimeout(run, 300) };
    },
    [attach, cancel]
  );
}